import type { BackendApiErrorUnion } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import type {
  OrderSummaryAdapters,
  PaymentRequestAdapters,
} from '@vs-shop/engine/module/order/orderAdapters';
import { makeOrderRequests } from '@vs-shop/engine/module/order/makeOrderRequests';
import type { StripJsonLd } from '@vs-shop/schemas/transform/withoutJsonLd';
import { PaymentRequestExtended } from '../schema/branded/paymentRequestTightened';
import {
  OrderDetail,
  OrderDetailPaid,
  OrderIndex,
  OrderShow,
  ShippingOptions,
} from '../schema/openapi/backend';

/**
 * Seam-C (workspace#49 option C, layer 3) — order request composition root.
 *
 * Binds the engine's `makeOrderRequests` factory with rcprofi's tightened
 * response schemas and the adapter projections the engine's Debug logs need.
 *
 * This module is deliberately a leaf: it imports NO flow, atom, cookie, or
 * runtime. That lets `flow/order/getOrder` import the bound `getOrderExpanded`
 * from here without forming a cycle with `bind/order.ts` (which imports
 * `getOrder`). See `packages/engine/src/module/order/makeOrderModule.ts` header.
 */

const orderSummaryAdapters: OrderSummaryAdapters<StripJsonLd<OrderShow>> = {
  number: (order) => order.number ?? null,
  tokenValue: (order) => order.tokenValue ?? null,
};

const paymentRequestAdapters: PaymentRequestAdapters<StripJsonLd<PaymentRequestExtended>> = {
  state: (paymentRequest) => paymentRequest.state,
  hash: (paymentRequest) => paymentRequest.hash ?? null,
  orderToken: (paymentRequest) => paymentRequest.orderToken,
};

const orderRequests = makeOrderRequests({
  orderShow: OrderShow,
  orderDetail: OrderDetail,
  orderDetailPaid: OrderDetailPaid,
  orderIndex: OrderIndex,
  shippingOptions: ShippingOptions,
  paymentRequestExtended: PaymentRequestExtended,
  orderSummaryAdapters,
  paymentRequestAdapters,
});

export const addProductVariantToOrder = orderRequests.addProductVariantToOrder;
export const AddProductVariantError = orderRequests.AddProductVariantError;
export type AddProductVariantError = BackendApiErrorUnion<typeof AddProductVariantError>;

export const completeOrder = orderRequests.completeOrder;
export const CompleteOrderError = orderRequests.CompleteOrderError;
export type CompleteOrderError = BackendApiErrorUnion<typeof CompleteOrderError>;

export const createOrder = orderRequests.createOrder();
export const CreateOrderError = orderRequests.CreateOrderError;
export type CreateOrderError = BackendApiErrorUnion<typeof CreateOrderError>;

export const createPaymentRequestForOrder = orderRequests.createPaymentRequestForOrder;
export const CreatePaymentRequestForOrderError = orderRequests.CreatePaymentRequestForOrderError;
export type OrderCreatePaymentRequestForOrderError = BackendApiErrorUnion<
  typeof CreatePaymentRequestForOrderError
>;

export const editProductVariantQuantityInOrder = orderRequests.editProductVariantQuantityInOrder;
export const EditProductVariantQuantityError = orderRequests.EditProductVariantQuantityError;
export type EditProductVariantQuantityError = BackendApiErrorUnion<
  typeof EditProductVariantQuantityError
>;

export const getOrderByIdentifierExpanded = orderRequests.getOrderByIdentifierExpanded;
export const GetOrderByIdentifierExpandedError = orderRequests.GetOrderByIdentifierExpandedError;
export type OrderGetByIdentifierExpandedError = BackendApiErrorUnion<
  typeof GetOrderByIdentifierExpandedError
>;

export const getOrderExpanded = orderRequests.getOrderExpanded;
export const GetOrderExpandedError = orderRequests.GetOrderExpandedError;
export type OrderGetOrderExpandedError = BackendApiErrorUnion<typeof GetOrderExpandedError>;

export const listOrdersForUser = orderRequests.listOrdersForUser;
export const ListOrdersForUserError = orderRequests.ListOrdersForUserError;
export type OrderListForUserError = BackendApiErrorUnion<typeof ListOrdersForUserError>;

export const getPaymentRequestByHashCheck = orderRequests.getPaymentRequestByHashCheck;
export const CheckPaymentRequestByHashError = orderRequests.CheckPaymentRequestByHashError;
export type OrderCheckPaymentRequestByHashError = BackendApiErrorUnion<
  typeof CheckPaymentRequestByHashError
>;

export const getPaymentRequestWithOrder = orderRequests.getPaymentRequestWithOrder;

export const listShippingAndPaymentsForOrder = orderRequests.listShippingAndPaymentsForOrder;
export const ListShippingAndPaymentsForOrderError =
  orderRequests.ListShippingAndPaymentsForOrderError;
export type ShippingAndPaymentListForOrderError = BackendApiErrorUnion<
  typeof ListShippingAndPaymentsForOrderError
>;

export const updateOrder = orderRequests.updateOrder;
export const UpdateOrderError = orderRequests.UpdateOrderError;
export type UpdateOrderError = BackendApiErrorUnion<typeof UpdateOrderError>;

export const updatePaymentInOrder = orderRequests.updatePaymentInOrder;
export const UpdatePaymentError = orderRequests.UpdatePaymentError;
export type UpdatePaymentError = BackendApiErrorUnion<typeof UpdatePaymentError>;

export const updateShippingInOrder = orderRequests.updateShippingInOrder;
export const UpdateShippingError = orderRequests.UpdateShippingError;
export type UpdateShippingError = BackendApiErrorUnion<typeof UpdateShippingError>;

// deleteProductVariantFromOrder already lives engine-side (moved in an earlier
// wave); re-exported here so the bind barrel is the single order-request surface.
export {
  deleteProductVariantFromOrder,
  DeleteProductVariantError,
} from '@vs-shop/engine/request/backendApi/order/deleteProductVariantFromOrder';

export type { CreatePaymentRequestPayload } from '@vs-shop/engine/module/order/makeOrderRequests';
